import React, {
  ReactElement,
  useLayoutEffect,
  useEffect,
  useMemo,
  useReducer,
  forwardRef,
} from "react";
import { reducer } from "./navReducer";
import { NavTreeActionType } from "./navReducer.types";
import { MenuItem } from "./menu-item";
import { PanelMenuProps, DataItem } from "./panel-menu.types";
import classNames from "classnames";
import { NavContext, initialState } from "./navContext";
import { NavTreeState } from "./navContext.types";
import { navItemsParser } from "./navParser";

export const renderMenuItem = (
  item: DataItem,
  isHeader: boolean = false,
  isHeaderSecondary: boolean = false,
  moreNavChild: boolean = false
): ReactElement | null => (
  <MenuItem
    key={item.id}
    {...item}
    _privateProps={{
      ...item._privateProps,
      isHeader,
      isHeaderSecondary,
      isTopLevelItem: moreNavChild ? false : item._privateProps?.isTopLevelItem,
    }}
  >
    {item.children &&
      item.children.length > 0 &&
      item.children?.map((item) =>
        renderMenuItem(item, isHeader, isHeaderSecondary, moreNavChild)
      )}
  </MenuItem>
);

export const PanelMenuStandalone = forwardRef<HTMLUListElement, PanelMenuProps>(
  ({ children, className, data, navAriaLabel }, navListRef) => {
    const [state, dispatch] = useReducer(reducer, initialState);

    const panelMenuTree = useMemo(
      () =>
        navItemsParser(
          data ? null : React.Children.toArray(children),
          data ?? null
        ),
      [children, data]
    );

    const childrenJsonString = JSON.stringify(panelMenuTree);

    const value: NavTreeState = {
      ...state,
      addActive: (itemId: string): void => {
        dispatch({ type: NavTreeActionType.ADD_ACTIVE, itemId });
      },
      toggleExpand: (itemId: string): void => {
        dispatch({ type: NavTreeActionType.TOGGLE_EXPAND, itemId });
      },
    };

    useEffect(() => {
      if (dispatch) {
        dispatch({
          type: NavTreeActionType.REPLACE_STATE,
          changedTree: panelMenuTree,
        });
      }
      /**
       * stringify version of tree is needed for comparision in dependency aray
       * because `onClick` function is breaking object comparison on every re-render
       * (callback reference changes, which gives always `true`).
       * useDeepCompareEffect also don't work here.
       */
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [childrenJsonString]);

    useLayoutEffect(() => {
      /**
       * Transition animation breaks some Header's features so
       * it's added only to standalone PanelMenu
       */

      const transtionStylesExist = document.querySelector(
        "#spark-react-menu__transition-styles"
      );

      if (!transtionStylesExist) {
        const css = `
          <style id="spark-react-menu__transition-styles">
            *:not(header.spark-header--visible > nav) > .spark-menu ul.spark-menu__list_transition {
              -webkit-transition: height 250ms cubic-bezier(0.86, 0, 0.07, 1) 0s, opacity 175ms ease-in 100ms;
              transition: height 250ms cubic-bezier(0.86, 0, 0.07, 1) 0s, opacity 175ms ease-in 100ms;
              overflow: hidden;
              height: 0px;
            }
          </style>
        `;
        document.head.insertAdjacentHTML("beforeend", css);
      }
    }, []);

    return (
      <nav
        className={classNames("spark-menu spark-menu--panel", className)}
        aria-label={navAriaLabel}
      >
        <NavContext.Provider value={{ state: value, dispatch }}>
          <ul ref={navListRef} className="spark-menu__list">
            {state.navTree.map((item) => renderMenuItem(item))}
          </ul>
        </NavContext.Provider>
      </nav>
    );
  }
);

PanelMenuStandalone.displayName = "PanelMenuStandalone";
